// Folding Table — the multiplayer waiting room.
//
// Design language: a chabudai (low Japanese table) seen from above.
// Cream washi mat with seigaiha pattern at the center, players seated
// in a full circle around it, lobby code on a cream plaque in the
// middle with wooden tile letters. Vermilion lacquer for host marker,
// jade glow for ready state. The reference is Lobby2V2_A from
// assets/samples/lobby-alternatives-v2.jsx — adapted for 8 seats
// (vs the reference's 6), no chat scroll (the right column is
// reclaimed for a centered layout), and a single 100vh page.

import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react'
import { motion } from 'framer-motion'
import { GLYPHS } from '../design-system'
import type { GlyphKey, LobbyParticipantPublic } from '@origami/shared'
import { useMultiStore } from '../multi/multiStore'
import { RotatePrompt, useIsFoldOpen, useIsMobile } from './useIsMobile'
import './LobbyScreen.css'

const MAX_SEATS = 8

/** Seat-fill order around the round table, expressed as angles in
 *  degrees (0 = right / 3 o'clock, -90 = top / 12 o'clock).
 *
 *  Players fill left / right first (9 then 3 o'clock), then the
 *  four diagonal seats alternating top↔bottom for visual balance,
 *  and finally the 12 and 6 o'clock slots last. The top + bottom
 *  positions are the ones most likely to be clipped on short
 *  mobile windows, so we save them for when the table is nearly
 *  full and the player density justifies the trade-off.
 *
 *  Indexed by participant position (0 = host / first joined). */
const SEAT_ANGLES_DEG = [
  180,   // 0 — left (9 o'clock)
  0,     // 1 — right (3 o'clock)
  -45,   // 2 — top-right diagonal (1:30)
  135,   // 3 — bottom-left diagonal (7:30)
  -135,  // 4 — top-left diagonal (10:30)
  45,    // 5 — bottom-right diagonal (4:30)
  -90,   // 6 — top (12 o'clock)
  90,    // 7 — bottom (6 o'clock)
] as const

/** Alternate seat angles + radius multipliers for SHORT viewports
 *  (CSS media query @max-height: 380px). The 12 + 6 o'clock seats
 *  get clipped above + below the painted backdrop's central circle
 *  on short windows, so we relocate them to "far left" and "far
 *  right" — same angle as the 9 and 3 o'clock seats but pushed
 *  outward on a larger radius. The diagonals stay at their normal
 *  positions; only seats 6 + 7 differ between layouts. */
const SHORT_HEIGHT_SEAT_ANGLES_DEG = [
  180,   // 0
  0,     // 1
  -45,   // 2
  135,   // 3
  -135,  // 4
  45,    // 5
  180,   // 6 — was top, now far-left (same angle as seat 0)
  0,     // 7 — was bottom, now far-right (same angle as seat 1)
] as const

const SHORT_HEIGHT_SEAT_RADIUS_MULT = [
  1,    // 0
  1,    // 1
  1,    // 2
  1,    // 3
  1,    // 4
  1,    // 5
  1.7,  // 6 — pushed well outward beyond seat 0 for visual gap
  1.7,  // 7 — pushed well outward beyond seat 1
] as const

interface LobbyScreenProps {
  onSessionLost: () => void
}

export function LobbyScreen({ onSessionLost }: LobbyScreenProps): ReactElement | null {
  const session = useMultiStore((s) => s.session)
  const error = useMultiStore((s) => s.error)
  const isMobile = useIsMobile()
  const foldOpen = useIsFoldOpen()

  useEffect(() => {
    if (error === 'no_session') onSessionLost()
  }, [error, onSessionLost])

  if (!session) return null
  const lobby = session.lobby
  const localPlayerId = useMemo(
    () =>
      lobby.participants.find((p) => p.participant_id === session.participant_id)
        ?.player_id ?? '',
    [lobby, session.participant_id],
  )
  const isHost = lobby.host_player_id === localPlayerId
  const localParticipantId = session.participant_id
  const localReady = session.ready
  const allReady =
    lobby.participants.length > 0 && lobby.participants.every((p) => p.ready)

  // Build a fixed-length seat array. Filled positions take seats in
  // join order; remaining slots show "open seat" placeholders so the
  // table always reads as MAX_SEATS even with a half-full lobby.
  const seats: (LobbyParticipantPublic | null)[] = [
    ...lobby.participants.slice(0, MAX_SEATS),
    ...Array.from(
      { length: Math.max(0, MAX_SEATS - lobby.participants.length) },
      () => null,
    ),
  ]

  if (foldOpen) {
    return (
      <FoldLobby
        seats={seats}
        lobbyCode={lobby.lobby_code}
        roundTimer={lobby.round_timer_seconds}
        localParticipantId={localParticipantId}
        isHost={isHost}
        aliveCount={lobby.participants.length}
        localReady={localReady}
        allReady={allReady}
      />
    )
  }

  return (
    <div className={`lb-page${isMobile ? ' lb-page--compact' : ''}`}>
      {isMobile && <RotatePrompt />}
      <TopBanner lobbyCode={lobby.lobby_code} roundTimer={lobby.round_timer_seconds} />
      <TableArena
        seats={seats}
        lobbyCode={lobby.lobby_code}
        localParticipantId={localParticipantId}
      />
      <Footer
        isHost={isHost}
        aliveCount={lobby.participants.length}
        localReady={localReady}
        allReady={allReady}
      />
    </div>
  )
}

// ---- Top banner ------------------------------------------------------

function TopBanner({
  lobbyCode,
  roundTimer,
}: {
  lobbyCode: string
  roundTimer: number | null
}): ReactElement {
  const leave = useMultiStore((s) => s.leave)
  // lobbyCode is no longer painted here — the central wooden tiles
  // own that. Voiding the prop keeps the call site contract stable
  // without complicating the parent's spread.
  void lobbyCode
  return (
    <header className="lb-banner">
      <div className="lb-banner__left">
        <button type="button" className="lb-banner__leave" onClick={() => void leave()}>
          ‹ Leave
        </button>
      </div>
      <div className="lb-banner__title-wrap">
        <span className="lb-banner__kanji" aria-hidden>
          卓
        </span>
        <div className="lb-banner__title-stack">
          <h1 className="lb-banner__title">Folding Table</h1>
          <span className="lb-banner__sub">WAITING ROOM</span>
        </div>
      </div>
      <div className="lb-banner__right">
        <StatBlock
          kanji="時"
          value={roundTimer == null ? 'manual' : `${roundTimer}s`}
          label={roundTimer == null ? 'ready check' : 'round'}
        />
      </div>
    </header>
  )
}

/** Read-only info display — bare typography, no chrome. The
 *  vertical hairline between two stat blocks reads as "scoreboard
 *  readout" so the data points can't be confused with the
 *  cream-pill Leave button on the opposite corner. */
function StatBlock({
  kanji,
  value,
  label,
}: {
  kanji: string
  value: string
  label: string
}): ReactElement {
  return (
    <div className="lb-stat">
      <span className="lb-stat__kanji" aria-hidden>
        {kanji}
      </span>
      <div className="lb-stat__col">
        <span className="lb-stat__value">{value}</span>
        <span className="lb-stat__label">{label}</span>
      </div>
    </div>
  )
}

// ---- Table arena -----------------------------------------------------

function TableArena({
  seats,
  lobbyCode,
  localParticipantId,
}: {
  seats: (LobbyParticipantPublic | null)[]
  lobbyCode: string
  localParticipantId: string
}): ReactElement {
  const readyCount = seats.filter((s) => s && s.ready).length
  const totalAlive = seats.filter((s) => s && !s.is_eliminated).length
  return (
    <main className="lb-arena">
      <ReadyRing readyCount={readyCount} totalAlive={totalAlive} />
      <div className="lb-mat" aria-hidden>
        <div className="lb-mat__pattern" />
        <span className="lb-mat__watermark" aria-hidden>
          待
        </span>
      </div>
      <CenterPlaque code={lobbyCode} ready={readyCount} alive={totalAlive} />
      {seats.map((seat, i) => {
        // Seat 0 → left, seat 1 → right, then diagonals, then top
        // and bottom last (the slots that get clipped on tall-but-
        // narrow phone windows). See SEAT_ANGLES_DEG above.
        const angleDeg = SEAT_ANGLES_DEG[i] ?? -90
        const angleRad = (angleDeg * Math.PI) / 180
        // Short-viewport layout — seats 6 + 7 move to "far left" and
        // "far right" instead of top + bottom. The CSS media query
        // at @max-height: 380px swaps which set is active.
        const shortAngleDeg = SHORT_HEIGHT_SEAT_ANGLES_DEG[i] ?? angleDeg
        const shortAngleRad = (shortAngleDeg * Math.PI) / 180
        const shortRadiusMult = SHORT_HEIGHT_SEAT_RADIUS_MULT[i] ?? 1
        const seatStyle: CSSProperties = {
          // The card is centered on a point at (--seat-r, angle) from
          // the arena center. CSS does the trig — we feed in the
          // pre-computed cos/sin so we don't have to repeat the math.
          '--seat-x': `${Math.cos(angleRad)}`,
          '--seat-y': `${Math.sin(angleRad)}`,
          '--seat-x-sh': `${Math.cos(shortAngleRad)}`,
          '--seat-y-sh': `${Math.sin(shortAngleRad)}`,
          '--seat-r-mult-sh': `${shortRadiusMult}`,
          '--seat-idx': `${i}`,
        } as CSSProperties
        return (
          <motion.div
            key={seat?.participant_id ?? `empty-${i}`}
            className="lb-seat-wrap"
            style={seatStyle}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                delay: 0.05 * i,
                duration: 0.35,
                ease: [0.2, 0.8, 0.2, 1],
              },
            }}
          >
            {seat ? (
              <SeatCard
                participant={seat}
                isYou={seat.participant_id === localParticipantId}
              />
            ) : (
              <EmptySeatCard />
            )}
          </motion.div>
        )
      })}
    </main>
  )
}

/** SVG ring that wraps the mat — progress grows clockwise from 12
 *  o'clock as more players ready up. */
function ReadyRing({
  readyCount,
  totalAlive,
}: {
  readyCount: number
  totalAlive: number
}): ReactElement {
  const pct = totalAlive > 0 ? readyCount / totalAlive : 0
  // viewBox is 100×100, ring r=46. circumference = 2πr ≈ 289.03.
  const C = 2 * Math.PI * 46
  return (
    <svg className="lb-ring" viewBox="0 0 100 100" aria-hidden>
      <circle
        cx="50"
        cy="50"
        r="46"
        fill="none"
        stroke="rgba(58, 42, 24, 0.18)"
        strokeWidth="1.5"
      />
      <circle
        cx="50"
        cy="50"
        r="46"
        fill="none"
        stroke="#5e8a5a"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={`${C * pct} ${C}`}
        transform="rotate(-90 50 50)"
        style={{ transition: 'stroke-dasharray 0.5s ease' }}
      />
    </svg>
  )
}

// ---- Center plaque + code tiles --------------------------------------

function CenterPlaque({
  code,
  ready,
  alive,
}: {
  code: string
  ready: number
  alive: number
}): ReactElement {
  const [copied, setCopied] = useState(false)
  const onCopy = () => {
    void navigator.clipboard?.writeText(code)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="lb-plaque">
      <span className="lb-plaque__stamp" aria-hidden>
        鍵
      </span>
      <span className="lb-plaque__cap tiny-cap">lobby code</span>
      <div className="lb-tiles" aria-label={`Lobby code ${code}`}>
        {code.padEnd(6, ' ').slice(0, 6).split('').map((ch, i) => (
          <span key={i} className="lb-tile">
            {ch.trim() || '·'}
          </span>
        ))}
      </div>
      <div className="lb-plaque__chips">
        <button type="button" className="lb-chip" onClick={onCopy}>
          <span className="lb-chip__glyph" aria-hidden>⧉</span>
          {copied ? 'copied!' : 'copy'}
        </button>
        <span className="lb-chip lb-chip--ready">
          <span className="lb-chip__glyph" aria-hidden>用</span>
          {ready}/{alive} ready
        </span>
      </div>
    </div>
  )
}

// ---- Seats -----------------------------------------------------------

function SeatCard({
  participant: p,
  isYou,
}: {
  participant: LobbyParticipantPublic
  isYou: boolean
}): ReactElement {
  const Glyph = GLYPHS[p.icon as GlyphKey] ?? GLYPHS.crane!
  const ready = p.ready
  const classes = [
    'lb-seat',
    ready ? 'lb-seat--ready' : 'lb-seat--waiting',
    isYou ? 'lb-seat--you' : '',
    p.is_host ? 'lb-seat--host' : '',
    p.is_eliminated ? 'lb-seat--out' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={classes}>
      <header className="lb-seat__name-band">
        <span className="lb-seat__name" title={p.display_name}>
          {p.display_name}
        </span>
        <span
          className={`lb-seat__status${ready ? ' lb-seat__status--ready' : ''}`}
          aria-label={ready ? 'ready' : 'waiting'}
        />
      </header>
      <div className="lb-seat__art">
        <div className="lb-seat__art-bg" aria-hidden />
        <div className="lb-seat__glyph" aria-hidden>
          <Glyph size={72} />
        </div>
        {isYou && (
          <span className="lb-seat__watermark lb-seat__watermark--you" aria-hidden>
            君
          </span>
        )}
      </div>
      <footer className="lb-seat__team-band">
        <span className="lb-seat__team" title={p.team_name}>
          {p.team_name}
        </span>
      </footer>
      {p.is_host && (
        <span className="lb-seat__host-fold" aria-label="host">
          <span className="lb-seat__host-kanji" aria-hidden>主</span>
        </span>
      )}
    </div>
  )
}

function EmptySeatCard(): ReactElement {
  return (
    <div className="lb-seat lb-seat--empty" aria-label="open seat">
      <div className="lb-seat__empty-icon" aria-hidden>＋</div>
      <div className="lb-seat__empty-label">open seat</div>
      <div className="lb-seat__empty-hint tiny-cap">share the code</div>
    </div>
  )
}

// ---- Footer ----------------------------------------------------------

function Footer({
  isHost,
  aliveCount,
  localReady,
  allReady,
}: {
  isHost: boolean
  aliveCount: number
  localReady: boolean
  allReady: boolean
}): ReactElement {
  const start = useMultiStore((s) => s.start)
  const ready = useMultiStore((s) => s.ready)
  const error = useMultiStore((s) => s.error)
  const enoughPlayers = aliveCount >= 2
  const startEnabled = isHost && enoughPlayers && allReady
  return (
    <footer className="lb-footer">
      <button
        type="button"
        className={`lb-footer__ready${localReady ? ' lb-footer__ready--on' : ''}`}
        onClick={() => void ready()}
        aria-pressed={localReady}
      >
        <span className="lb-footer__ready-kanji" aria-hidden>用</span>
        {localReady ? "I'm ready" : 'Mark me ready'}
      </button>

      {isHost && (
        <button
          type="button"
          className="lb-footer__start"
          disabled={!startEnabled}
          onClick={() => void start()}
          title={
            !enoughPlayers
              ? 'Need at least 2 players seated'
              : !allReady
                ? 'Waiting for everyone to ready up'
                : 'Start the match'
          }
        >
          <span className="lb-footer__start-kanji" aria-hidden>始</span>
          {!enoughPlayers
            ? `Need ${2 - aliveCount} more`
            : !allReady
              ? 'Waiting on others…'
              : 'Start the match'}
        </button>
      )}

      {!isHost && (
        <span className="lb-footer__waiting">
          <span className="lb-footer__waiting-dot" aria-hidden />
          {allReady
            ? 'All folded — waiting for the host to start…'
            : 'Mark yourself ready when you’re set.'}
        </span>
      )}

      {error === 'not_all_ready' && (
        <span className="lb-footer__hint">someone's still folding their scroll</span>
      )}
    </footer>
  )
}

// === Fold layout =====================================================
//
// Galaxy Fold inner display in landscape (~828x690 CSS px). Hinge is
// HORIZONTAL across y=50% (player rotates 90° to satisfy our landscape
// lock). Forked at JSX level via useIsFoldOpen() — see CLAUDE.md
// "Fold-only layout fork via JSX". The radial round-table layout
// can't survive the fold (the mat lands on the hinge and the 12/6
// o'clock cards cross it), so this variant drops the table metaphor
// in favor of two seat rows: positions 0-3 in the top half, 4-7 in
// the bottom half. The lobby-code plaque sits as a horizontal band
// inside the top half (not crossing the crease). The footer's
// Ready/Start controls live in the bottom half.

interface FoldLobbyProps {
  seats: (LobbyParticipantPublic | null)[]
  lobbyCode: string
  roundTimer: number | null
  localParticipantId: string
  isHost: boolean
  aliveCount: number
  localReady: boolean
  allReady: boolean
}

function FoldLobby({
  seats,
  lobbyCode,
  roundTimer,
  localParticipantId,
  isHost,
  aliveCount,
  localReady,
  allReady,
}: FoldLobbyProps): ReactElement {
  const readyCount = seats.filter((s) => s && s.ready).length
  const aliveSeated = seats.filter((s) => s && !s.is_eliminated).length
  return (
    <div className="lb-page lb-fold-page">
      <section className="lb-fold-top">
        <TopBanner lobbyCode={lobbyCode} roundTimer={roundTimer} />
        <FoldPlaque code={lobbyCode} ready={readyCount} alive={aliveSeated} />
        <div className="lb-fold-row">
          {seats.slice(0, 4).map((seat, i) => (
            <motion.div
              key={seat?.participant_id ?? `empty-${i}`}
              className="lb-fold-seat-wrap"
              initial={{ opacity: 0, y: -10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.04 * i,
                  duration: 0.3,
                  ease: [0.2, 0.8, 0.2, 1],
                },
              }}
            >
              {seat ? (
                <SeatCard
                  participant={seat}
                  isYou={seat.participant_id === localParticipantId}
                />
              ) : (
                <EmptySeatCard />
              )}
            </motion.div>
          ))}
        </div>
      </section>

      <div className="lb-fold-hinge" aria-hidden />

      <section className="lb-fold-bottom">
        <div className="lb-fold-row">
          {seats.slice(4, 8).map((seat, i) => {
            const idx = i + 4
            return (
              <motion.div
                key={seat?.participant_id ?? `empty-${idx}`}
                className="lb-fold-seat-wrap"
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.04 * idx,
                    duration: 0.3,
                    ease: [0.2, 0.8, 0.2, 1],
                  },
                }}
              >
                {seat ? (
                  <SeatCard
                    participant={seat}
                    isYou={seat.participant_id === localParticipantId}
                  />
                ) : (
                  <EmptySeatCard />
                )}
              </motion.div>
            )
          })}
        </div>
        <Footer
          isHost={isHost}
          aliveCount={aliveCount}
          localReady={localReady}
          allReady={allReady}
        />
      </section>
    </div>
  )
}

/** Lobby-code plaque flattened into a horizontal band for the Fold
 *  top half. Same wooden tiles + chips as the desktop centered plaque
 *  but laid out inline so it sits between the top banner and the
 *  seat row without absolute positioning (which would land it on the
 *  hinge). */
function FoldPlaque({
  code,
  ready,
  alive,
}: {
  code: string
  ready: number
  alive: number
}): ReactElement {
  const [copied, setCopied] = useState(false)
  const onCopy = () => {
    void navigator.clipboard?.writeText(code)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="lb-fold-plaque">
      <span className="lb-fold-plaque__cap tiny-cap">lobby code</span>
      <div className="lb-tiles" aria-label={`Lobby code ${code}`}>
        {code
          .padEnd(6, ' ')
          .slice(0, 6)
          .split('')
          .map((ch, i) => (
            <span key={i} className="lb-tile">
              {ch.trim() || '·'}
            </span>
          ))}
      </div>
      <div className="lb-fold-plaque__chips">
        <button type="button" className="lb-chip" onClick={onCopy}>
          <span className="lb-chip__glyph" aria-hidden>⧉</span>
          {copied ? 'copied!' : 'copy'}
        </button>
        <span className="lb-chip lb-chip--ready">
          <span className="lb-chip__glyph" aria-hidden>用</span>
          {ready}/{alive} ready
        </span>
      </div>
    </div>
  )
}

